/**
 * Mitacs institution matching and candidate eligibility rules.
 */

const INSTITUTION_ABBREVIATIONS = {
  uoft: 'university of toronto',
  'u of t': 'university of toronto',
  ubc: 'university of british columbia',
  mcgill: 'mcgill university',
  uwaterloo: 'university of waterloo',
  'u of w': 'university of waterloo',
  uw: 'university of waterloo',
  queens: "queen's university",
  mcmaster: 'mcmaster university',
  western: 'western university',
  ualberta: 'university of alberta',
  uofc: 'university of calgary',
  uottawa: 'university of ottawa',
  concordia: 'concordia university',
  sfu: 'simon fraser university',
  uvic: 'university of victoria',
  yorku: 'york university',
  carleton: 'carleton university',
  dalhousie: 'dalhousie university',
  laval: 'universite laval',
  udem: 'universite de montreal',
  polytechnique: 'polytechnique montreal',
};

const STRIP_WORDS =
  /\b(university|universite|université|college|institute|institut|polytechnique|school|of|the|de|du|des|la|le|les)\b/gi;

const expandAbbreviation = (normalized) => {
  if (!normalized) return normalized;
  if (INSTITUTION_ABBREVIATIONS[normalized]) {
    return INSTITUTION_ABBREVIATIONS[normalized];
  }

  const tokens = normalized.split(' ').filter(Boolean);
  if (tokens.length === 1 && INSTITUTION_ABBREVIATIONS[tokens[0]]) {
    return INSTITUTION_ABBREVIATIONS[tokens[0]];
  }

  return normalized;
};

export const normalizeInstitutionName = (name) => {
  let normalized = String(name || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[''´`]/g, '')
    .replace(/\([^)]*\)/g, ' ')
    .replace(/[.,\-–—/|&:]/g, ' ')
    .replace(STRIP_WORDS, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return expandAbbreviation(normalized);
};

const getInstitutionLabel = (institution) => {
  const fields = institution?.fields || {};
  const raw =
    fields.Institution ||
    fields.institution ||
    fields.Name ||
    fields.name ||
    '';
  return String(raw).trim();
};

const tokenize = (name) =>
  normalizeInstitutionName(name)
    .split(' ')
    .filter((token) => token.length > 1);

const tokenOverlapScore = (a, b) => {
  const tokensA = tokenize(a);
  const tokensB = tokenize(b);
  if (!tokensA.length || !tokensB.length) return 0;

  const overlap = tokensA.filter((token) => tokensB.includes(token)).length;
  return overlap / Math.max(tokensA.length, tokensB.length);
};

const buildUniversitySearchVariants = (university) => {
  const raw = String(university || '').trim();
  if (!raw) return [];

  const variants = new Set([raw]);

  raw
    .split(/\s*[,–—-]\s*/)
    .map((part) => part.trim())
    .filter(Boolean)
    .forEach((part) => variants.add(part));

  return [...variants];
};

const findExactMatch = (normalizedUniversity, institutions) =>
  institutions.find(
    (inst) => normalizeInstitutionName(getInstitutionLabel(inst)) === normalizedUniversity
  ) || null;

const findSubstringMatch = (normalizedUniversity, institutions) =>
  institutions.find((inst) => {
    const instName = normalizeInstitutionName(getInstitutionLabel(inst));
    if (!instName) return false;
    return (
      normalizedUniversity.includes(instName) || instName.includes(normalizedUniversity)
    );
  }) || null;

const findTokenOverlapMatch = (university, institutions, threshold = 0.6) => {
  let bestMatch = null;
  let bestScore = threshold;

  for (const inst of institutions) {
    const instName = getInstitutionLabel(inst);
    const score = tokenOverlapScore(university, instName);
    if (score > bestScore) {
      bestScore = score;
      bestMatch = inst;
    }
  }

  return bestMatch;
};

/**
 * Match a parsed university name against MitacsInstitutions records.
 */
export function matchMitacsInstitution(university, institutions = []) {
  if (!university || !institutions.length) return null;

  const searchVariants = buildUniversitySearchVariants(university);

  for (const variant of searchVariants) {
    const normalizedUniversity = normalizeInstitutionName(variant);
    if (!normalizedUniversity) continue;

    const exactMatch = findExactMatch(normalizedUniversity, institutions);
    if (exactMatch) return exactMatch;

    const substringMatch = findSubstringMatch(normalizedUniversity, institutions);
    if (substringMatch) return substringMatch;

    const tokenMatch = findTokenOverlapMatch(variant, institutions);
    if (tokenMatch) return tokenMatch;
  }

  return null;
}

/**
 * Parse graduation date string (YYYY-MM, YYYY, or ISO) to { year, month }.
 * Month is 1–12. Defaults missing month to June for year-only values.
 */
export function parseGraduationYearMonth(value) {
  if (!value) return null;

  const str = String(value).trim();
  if (!str || str.toLowerCase() === 'null' || str.toLowerCase() === 'unknown') {
    return null;
  }

  if (/^\d{4}-\d{2}$/.test(str)) {
    const [year, month] = str.split('-').map(Number);
    if (year >= 1900 && month >= 1 && month <= 12) {
      return { year, month };
    }
    return null;
  }

  if (/^\d{4}$/.test(str)) {
    return { year: Number(str), month: 6 };
  }

  const date = new Date(str);
  if (isNaN(date.getTime())) return null;
  return { year: date.getFullYear(), month: date.getMonth() + 1 };
}

/** Format { year, month } as "Month YYYY" for display. */
export function formatYearMonth({ year, month }) {
  if (!year) return '';
  const date = new Date(year, (month || 1) - 1, 1);
  return date.toLocaleDateString('en-CA', { month: 'long', year: 'numeric' });
}

/**
 * Months elapsed from graduation month/year to the reference month/year.
 * Positive = graduated in the past; negative = graduation is in the future.
 */
export function monthsSinceGraduation(graduationValue, referenceDate = new Date()) {
  const grad = parseGraduationYearMonth(graduationValue);
  if (!grad) return null;

  const refYear = referenceDate.getFullYear();
  const refMonth = referenceDate.getMonth() + 1;

  return (refYear - grad.year) * 12 + (refMonth - grad.month);
}

/**
 * Whether graduation month/year is strictly after the reference month/year.
 */
export function isGraduationInFuture(graduationValue, referenceDate = new Date()) {
  const months = monthsSinceGraduation(graduationValue, referenceDate);
  return months !== null && months < 0;
}

/**
 * Check candidate eligibility against a matched Mitacs institution record.
 *
 * CurrentlyEnrolledOnly values:
 *   - "Current Only" — must be currently enrolled (or future graduation)
 *   - "Within 2 Years of Graduation" — enrolled OR graduated within 24 months
 *     (compared by graduation month/year vs current month/year)
 */
export function checkMitacsCandidateEligibility(
  institution,
  candidate,
  referenceDate = new Date()
) {
  if (!institution) {
    return {
      eligible: false,
      reason: 'University not found in Mitacs partner institutions',
      matchedInstitution: null,
    };
  }

  const rule = institution.fields?.CurrentlyEnrolledOnly || '';
  const isEnrolled = Boolean(candidate?.isCurrentlyEnrolled);
  const gradYm = parseGraduationYearMonth(candidate?.graduationDate);
  const institutionName = getInstitutionLabel(institution) || 'Unknown institution';
  const refLabel = formatYearMonth({
    year: referenceDate.getFullYear(),
    month: referenceDate.getMonth() + 1,
  });
  const gradLabel = gradYm ? formatYearMonth(gradYm) : null;

  if (rule === 'Current Only') {
    const futureGrad = isGraduationInFuture(candidate?.graduationDate, referenceDate);
    const eligible = isEnrolled || futureGrad;
    return {
      eligible,
      reason: eligible
        ? futureGrad
          ? `Expected graduation ${gradLabel} — after ${refLabel} (Current Only partner)`
          : `Currently enrolled at ${institutionName} (Current Only partner)`
        : `${institutionName} requires current enrollment as of ${refLabel}`,
      matchedInstitution: institution,
    };
  }

  if (rule === 'Within 2 Years of Graduation') {
    if (isEnrolled) {
      return {
        eligible: true,
        reason: `Currently enrolled at ${institutionName}`,
        matchedInstitution: institution,
      };
    }

    if (!gradYm) {
      return {
        eligible: false,
        reason: `Could not verify graduation date for ${institutionName} (requires enrollment or graduation within 2 years of ${refLabel})`,
        matchedInstitution: institution,
      };
    }

    const months = monthsSinceGraduation(candidate?.graduationDate, referenceDate);

    // Future graduation counts as currently enrolled
    if (months !== null && months < 0) {
      return {
        eligible: true,
        reason: `Expected graduation ${gradLabel} — after ${refLabel}`,
        matchedInstitution: institution,
      };
    }

    const eligible = months !== null && months <= 24;

    return {
      eligible,
      reason: eligible
        ? `Graduated ${gradLabel} — within 24 months of ${refLabel} (${months} mo.)`
        : `Graduated ${gradLabel} — more than 24 months before ${refLabel} (${months} mo.)`,
      matchedInstitution: institution,
    };
  }

  return {
    eligible: false,
    reason: `Unknown enrollment rule "${rule}" for ${institutionName}`,
    matchedInstitution: institution,
  };
}

/**
 * Full Mitacs eligibility check from parsed candidate + institutions list.
 */
export function evaluateMitacsEligibility(candidate, institutions = []) {
  const university = candidate?.university;
  if (!university) {
    return {
      eligible: false,
      reason: 'Could not identify university from resume',
      matchedInstitution: null,
    };
  }

  const matchedInstitution = matchMitacsInstitution(university, institutions);
  return checkMitacsCandidateEligibility(matchedInstitution, candidate);
}

/** Extract display-friendly fields from a matched MitacsInstitutions record. */
export function getMitacsInstitutionDetails(institution) {
  if (!institution?.fields) return null;
  const f = institution.fields;
  return {
    name: getInstitutionLabel(institution),
    website: f.Website || '',
    facultyPageUrl: f.FacultyPageURL || '',
    researchPageUrl: f.ResearchPageURL || '',
    notes: f.Notes || '',
    enrollmentRule: f.CurrentlyEnrolledOnly || '',
  };
}
